// THE DOCUMENT-LANGUAGE NORMALISER — one home, both ends.
//
// 206E §4.3 [RULED 2026-09-23] — a pro forma's words are picked in the CLIENT's
// language at creation (the title in its name, the particular-conditions
// prefill), "normalised as `normalizeLang` does" — and that function lived in
// server/src/lib/docGen.js, where the server resolves a document's language
// from `Client.language`. The window now needs the same answer, so the rule
// moves here, in the shape `folderName.mjs` and `caseRefPattern.mjs` already
// use for a rule both ends must spell alike: a pure ESM module the client
// imports and the server requires (docGen.js re-exports it under its own name,
// so its three callers are untouched). An EXTRACTION: the body below is
// byte-for-byte the one that was in docGen.js.

/** Map a free-form Client.language (or a generation override) onto a template
 *  variant language code (nl/fr/en), or null if it doesn't resolve. */
export function normalizeLang(s) {
  const v = String(s || '').trim().toLowerCase();
  if (!v) return null;
  if (v.startsWith('nl') || v.startsWith('du') || v.includes('neder') || v.includes('vlaam')) return 'nl';
  if (v.startsWith('fr') || v.includes('fran')) return 'fr';
  if (v.startsWith('en') || v.includes('angl') || v.includes('engl')) return 'en';
  return null;
}

/* 207C §6 made French the one answer for a client without a language
   (`CLIENT_LANGUAGE_DEFAULT`, which stood here), and 208 §11 made it the answer
   for text addressed to no single client. 209 §5 [RULED 18, 2026-09-25]
   REPLACES BOTH: « Text addressed to a client is in that client's language; a
   client without one reads its case's language; every other text is in the
   case's language. No language is ever assumed. » Every case and collective
   case carries its own (`language`, chosen when it is created); a text that
   needs a language and finds none is refused (`case.language_required`) — never
   a guess. (One reader keeps its RULED answer: a WhatsApp entry, the importer's
   language — ruling 10 of 2026-09-24.) */

/** A language's name in the reader's language, from the platform; the code when it has none.
 *  210 §10 — MOVED here from src/lib/caseLanguage.ts, verbatim, so both ends read it.
 *  @param {string} code
 *  @param {string} readerLang
 *  @returns {string} */
export function languageName(code, readerLang) {
  try {
    const name = new globalThis.Intl.DisplayNames([readerLang], { type: 'language' }).of(code);
    if (name && name !== code) return name.charAt(0).toLocaleUpperCase(readerLang) + name.slice(1);
  } catch {
    /* an unknown language tag: its code */
  }
  return code;
}

/** 209 §19 [ADVISOR] — an import cell's case language: a code the dictionary carries,
 *  or one of the names the case form offers for it (languageOptions, caseLanguage.ts),
 *  in ANY of the app's languages — a file is written by whoever writes it
 *  (« Néerlandais », « Nederlands », "Dutch", « nl »), compared without case or
 *  accents. Anything else is no language: the case is created « à choisir », as the
 *  form leaves it. 210 §10 — MOVED here from src/lib/caseLanguage.ts, verbatim.
 *  @param {unknown} cell
 *  @param {readonly string[]} available
 *  @returns {string|null} */
export function importedCaseLanguage(cell, available) {
  const bare = (/** @type {string} */ s) => s.normalize('NFD').replace(/\p{M}/gu, '').trim().toLowerCase();
  let raw = '';
  if (typeof cell === 'string') raw = cell;
  else if (typeof cell === 'number') raw = String(cell);
  const v = bare(raw);
  if (!v) return null;
  for (const code of available) {
    if (v === bare(code)) return code;
    if (available.some((reader) => bare(languageName(code, reader)) === v)) return code;
  }
  return null;
}

/** 207C §6 — a client's language AS STORED. The server sends `language`; a row
 *  this page saved carries `lang` too (the API's alias, server/src/routes/
 *  clients.js), and every browser reader read `lang` alone — so every stored
 *  client read as having none. The server's first; the code as stored (« de »
 *  included), or "" when none.
 *  @param {unknown} c  a client row
 *  @returns {string} */
export function storedClientLanguage(c) {
  if (!c || typeof c !== 'object') return '';
  const row = /** @type {{ language?: unknown, lang?: unknown }} */ (c);
  const v = row.language ?? row.lang;
  return typeof v === 'string' ? v.trim() : '';
}

/** 209 §5 [RULED 18] — A CASE'S (or a collective case's) LANGUAGE AS STORED: its
 *  code, lower-case, or null when none was chosen (a case from before 209 the
 *  backfill could not name). Never a default.
 *  @param {unknown} cs  a case or collective case row
 *  @returns {string|null} */
export function storedCaseLanguage(cs) {
  if (!cs || typeof cs !== 'object') return null;
  const v = /** @type {{ language?: unknown }} */ (cs).language;
  return typeof v === 'string' && v.trim() ? v.trim().toLowerCase() : null;
}

/** 208 §11 [RULED 9], as amended by 209 §5 [RULED 18] — THE LANGUAGE A TEXT
 *  ADDRESSED TO A CLIENT IS WRITTEN IN (a pro forma's person title and
 *  particular conditions, the document itself): the client's own, normalised;
 *  else its case's — whoever opens it, so its words stay the same. NULL when
 *  neither has one: the caller refuses, it never guesses.
 *  @param {unknown} c   a client row
 *  @param {unknown} cs  its case or collective case row
 *  @returns {string|null} */
export function clientDocumentLanguage(c, cs) {
  return normalizeLang(storedClientLanguage(c)) ?? storedCaseLanguage(cs);
}
